import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import './AdminPanel.css';
import { BrandMark } from './Icons';
import { BRAND_NAME } from './brand';
import { ThemeToggle } from './theme';
import { ToastList, getApiErrorMessage } from './Toast';

const API_URL = process.env.REACT_APP_API_URL || '';

function AdminPanel() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(!!localStorage.getItem('token'));
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [editingVideo, setEditingVideo] = useState(null);
  const [detailedStats, setDetailedStats] = useState(null);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [stats, setStats] = useState({ total: 0, totalViews: 0, ready: 0 });
  const [userLists, setUserLists] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [allowedDomains, setAllowedDomains] = useState([]);
  const [blockedViewers, setBlockedViewers] = useState([]);
  const [viewLogs, setViewLogs] = useState([]);
  const [viewLogsVideoFilter, setViewLogsVideoFilter] = useState('');
  const [suspicious, setSuspicious] = useState(null);
  const [activeTab, setActiveTab] = useState('videos');
  const [newListName, setNewListName] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [newBlockedEmail, setNewBlockedEmail] = useState('');
  const [newBlockedName, setNewBlockedName] = useState('');
  const [selectedListForUpload, setSelectedListForUpload] = useState(null);
  const [xlsxFile, setXlsxFile] = useState(null);
  const [newPlaylistTitle, setNewPlaylistTitle] = useState('');
  const [newPlaylistDescription, setNewPlaylistDescription] = useState('');
  const [newPlaylistAccessByList, setNewPlaylistAccessByList] = useState(false);
  const [newPlaylistUserListId, setNewPlaylistUserListId] = useState('');
  const [editingPlaylist, setEditingPlaylist] = useState(null);
  const [editingUserList, setEditingUserList] = useState(null);
  const [newListItemFullName, setNewListItemFullName] = useState('');
  const [newListItemEmail, setNewListItemEmail] = useState('');
  const [editingListItemId, setEditingListItemId] = useState(null);
  const [editingListItemForm, setEditingListItemForm] = useState({ full_name: '', email: '' });
  const [toasts, setToasts] = useState([]);
  const toastCallbackRef = useRef(() => {});
  const [watermarkSettings, setWatermarkSettings] = useState({
    move_interval_seconds: 15,
    show_ip: true,
    opacity: 0.6,
    font_size: 12
  });

  const addToast = useCallback((type, text) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, type, text }]);
  }, []);
  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);
  useEffect(() => {
    toastCallbackRef.current = addToast;
  }, [addToast]);

  const authHeaders = () => ({ Authorization: `Bearer ${token}` });
  const getBaseUrl = () => window.location.origin;

  // При каждом изменении токена (начальная загрузка или после логина):
  // сначала валидируем токен, и только потом показываем панель управления.
  useEffect(() => {
    if (!token) {
      setAuthChecking(false);
      return;
    }
    axios
      .get(`${API_URL}/api/videos`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        setVideos(r.data);
        setIsAuthenticated(true);
        fetchUserLists();
        fetchPlaylists();
        fetchAllowedDomains();
        fetchBlockedViewers();
      })
      .catch(() => {
        // 401 обрабатывается глобальным перехватчиком (очищает токен).
        // Для прочих ошибок тоже показываем форму входа.
      })
      .finally(() => setAuthChecking(false));
  }, [token]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        if (err.response?.status === 401) {
          toastCallbackRef.current('info', 'Сессия истекла. Войдите снова.');
          setToken(null);
          localStorage.removeItem('token');
          setIsAuthenticated(false);
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  const fetchUserLists = () => {
    if (!token) return;
    axios.get(`${API_URL}/api/user-lists`, { headers: authHeaders() })
      .then((r) => setUserLists(r.data))
      .catch(() => {});
  };
  const fetchPlaylists = () => {
    if (!token) return;
    axios.get(`${API_URL}/api/playlists`, { headers: authHeaders() })
      .then((r) => setPlaylists(r.data))
      .catch(() => {});
  };
  const fetchAllowedDomains = () => {
    if (!token) return;
    axios.get(`${API_URL}/api/admin/allowed-domains`, { headers: authHeaders() })
      .then((r) => setAllowedDomains(r.data))
      .catch(() => {});
  };
  const fetchBlockedViewers = () => {
    if (!token) return;
    axios.get(`${API_URL}/api/admin/blocked-viewers`, { headers: authHeaders() })
      .then((r) => setBlockedViewers(r.data))
      .catch(() => {});
  };
  const fetchViewLogs = () => {
    const params = viewLogsVideoFilter ? { video_id: viewLogsVideoFilter } : {};
    axios.get(`${API_URL}/api/admin/view-logs`, { headers: authHeaders(), params: { limit: 500, ...params } })
      .then((r) => setViewLogs(r.data || []))
      .catch(() => setViewLogs([]));
  };
  const fetchSuspicious = () => {
    axios.get(`${API_URL}/api/admin/suspicious`, { headers: authHeaders() })
      .then((r) => setSuspicious(r.data))
      .catch(() => setSuspicious({ same_user_different_ips: [], mass_connections_24h: [] }));
  };

  useEffect(() => {
    if (videos.length > 0) {
      const totalViews = videos.reduce((sum, v) => sum + (v.views || 0), 0);
      const ready = videos.filter(v => v.status === 'ready').length;
      setStats({ total: videos.length, totalViews, ready });
    }
  }, [videos]);

  // Автообновление для видео в обработке
  useEffect(() => {
    const processingVideos = videos.filter(v => v.status === 'processing' || v.status === 'uploaded');
    if (processingVideos.length === 0 || !token) return;

    const interval = setInterval(() => {
      fetchVideos();
    }, 5000); // Обновляем каждые 5 секунд

    return () => clearInterval(interval);
  }, [videos, token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        username,
        password
      }, {
        headers: {
          'Content-Type': 'application/json'
        }
      });
      const newToken = response.data.access_token;
      setToken(newToken);
      localStorage.setItem('token', newToken);
      // isAuthenticated и данные установятся через useEffect([token])
    } catch (error) {
      console.error('Login error:', error);
      addToast('error', 'Ошибка входа: ' + getApiErrorMessage(error));
    }
  };

  const fetchVideos = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/videos`, { headers: authHeaders() });
      setVideos(response.data);
    } catch (error) {
      if (error.response?.status === 401) return;
      console.error('Error fetching videos:', error);
    }
  };

  const uploadVideo = async (e) => {
    e.preventDefault();
    if (!uploadFile) {
      addToast('error', 'Выберите файл');
      return;
    }

    setLoading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', uploadFile);
    if (uploadTitle) formData.append('title', uploadTitle);
    if (uploadDescription) formData.append('description', uploadDescription);

    try {
      await axios.post(`${API_URL}/api/videos/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total
          );
          setUploadProgress(percentCompleted);
        }
      });
      addToast('success', 'Видео загружено, начата конвертация');
      setUploadFile(null);
      setUploadTitle('');
      setUploadDescription('');
      setUploadProgress(0);
      fetchVideos().catch(() => {});
    } catch (error) {
      if (error.response?.status === 413) {
        addToast('error', 'Файл слишком большой. Максимальный размер: 2GB');
      } else {
        addToast('error', 'Ошибка загрузки: ' + getApiErrorMessage(error));
      }
      setUploadProgress(0);
    } finally {
      setLoading(false);
    }
  };

  const updateVideo = async (videoId, title, description, accessByList, userListId, embedOnly) => {
    try {
      await axios.put(`${API_URL}/api/videos/${videoId}`, {
        title,
        description,
        access_by_list: accessByList,
        user_list_id: userListId || null,
        embed_only: embedOnly
      }, { headers: authHeaders() });
      setEditingVideo(null);
      fetchVideos().catch(() => {});
      addToast('success', 'Видео сохранено');
    } catch (error) {
      if (error.response?.status === 401) return;
      addToast('error', 'Ошибка обновления: ' + getApiErrorMessage(error));
    }
  };

  const getPublicLink = (videoId) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/watch/${videoId}`;
  };

  const getEmbedCode = async (videoId) => {
    try {
      const response = await axios.get(`${API_URL}/api/videos/${videoId}/embed`, { headers: authHeaders() });
      setSelectedVideo(response.data);
    } catch (error) {
      if (error.response?.status === 401) return;
      addToast('error', 'Ошибка: ' + getApiErrorMessage(error));
    }
  };

  const deleteVideo = async (videoId) => {
    if (!window.confirm('Удалить видео?')) return;
    try {
      await axios.delete(`${API_URL}/api/videos/${videoId}`, { headers: authHeaders() });
      fetchVideos().catch(() => {});
      addToast('success', 'Видео удалено');
    } catch (error) {
      if (error.response?.status === 401) return;
      addToast('error', 'Ошибка удаления: ' + getApiErrorMessage(error));
    }
  };

  const triggerConvert = async (videoId) => {
    try {
      await axios.post(`${API_URL}/api/videos/${videoId}/convert`, {}, { headers: authHeaders() });
      addToast('success', 'Конвертация запущена');
      fetchVideos().catch(() => {});
    } catch (error) {
      if (error.response?.status === 401) return;
      addToast('error', 'Ошибка: ' + getApiErrorMessage(error));
    }
  };

  const getDetailedStats = async (videoId) => {
    try {
      const response = await axios.get(`${API_URL}/api/videos/${videoId}/detailed-stats`, { headers: authHeaders() });
      setDetailedStats(response.data);
    } catch (error) {
      if (error.response?.status === 401) return;
      addToast('error', 'Ошибка: ' + getApiErrorMessage(error));
    }
  };

  const logout = () => {
    setToken(null);
    localStorage.removeItem('token');
    setIsAuthenticated(false);
    setVideos([]);
  };

  const filteredVideos = videos.filter(video =>
    video.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (video.description && video.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (authChecking) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', background: '#0f0f13' }}>
        <div className="loading-spinner">
          <div className="spinner" />
          <p style={{ color: '#9a9aaa', marginTop: '1rem' }}>Проверка сессии...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <>
        <div className="login-container">
          <div className="login-card">
            <ThemeToggle className="login-theme-toggle" />
            <div className="login-card-header">
              <span className="login-logo"><BrandMark size={52} /></span>
              <h1 className="login-title">{BRAND_NAME}</h1>
              <p className="login-subtitle">Войдите в панель управления</p>
            </div>
            <form className="login-form" onSubmit={login}>
              <label className="login-label">
                <span>Имя пользователя</span>
                <input
                  type="text"
                  placeholder="admin"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  required
                  autoComplete="username"
                />
              </label>
              <label className="login-label">
                <span>Пароль</span>
                <input
                  type="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                />
              </label>
              <button type="submit" className="login-submit">Войти</button>
            </form>
          </div>
        </div>
        <ToastList toasts={toasts} removeToast={removeToast} />
      </>
    );
  }

  const downloadTemplate = () => {
    fetch(`${API_URL}/api/user-lists/template`, { headers: authHeaders() })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'user_list_template.xlsx';
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => addToast('error', 'Ошибка скачивания'));
  };

  const uploadXlsx = async () => {
    if (!selectedListForUpload || !xlsxFile) { addToast('error', 'Выберите список и файл XLSX'); return; }
    const fd = new FormData();
    fd.append('file', xlsxFile);
    try {
      await axios.post(`${API_URL}/api/user-lists/${selectedListForUpload}/upload-xlsx`, fd, { headers: authHeaders() });
      addToast('success', 'Загружено');
      setSelectedListForUpload(null);
      setXlsxFile(null);
      fetchUserLists();
    } catch (e) {
      addToast('error', getApiErrorMessage(e));
    }
  };

  const createUserList = async () => {
    if (!newListName.trim()) return;
    try {
      await axios.post(`${API_URL}/api/user-lists`, { name: newListName.trim() }, { headers: authHeaders() });
      setNewListName('');
      fetchUserLists();
      addToast('success', 'Список создан');
    } catch (e) {
      addToast('error', getApiErrorMessage(e));
    }
  };

  const addAllowedDomain = async () => {
    if (!newDomain.trim()) return;
    try {
      await axios.post(`${API_URL}/api/admin/allowed-domains`, { domain: newDomain.trim() }, { headers: authHeaders() });
      setNewDomain('');
      fetchAllowedDomains();
      addToast('success', 'Домен добавлен');
    } catch (e) {
      const msg = getApiErrorMessage(e);
      if (e.response?.status === 400 && (msg.includes('already') || msg.includes('уже'))) {
        addToast('info', msg);
        setNewDomain('');
        fetchAllowedDomains();
      } else {
        addToast('error', msg);
      }
    }
  };

  const blockViewer = async () => {
    if (!newBlockedEmail.trim()) return;
    const payload = { email: newBlockedEmail.trim(), full_name: (newBlockedName || '').trim() || null };
    try {
      await axios.post(`${API_URL}/api/admin/blocked-viewers`, payload, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' }
      });
      setNewBlockedEmail('');
      setNewBlockedName('');
      fetchBlockedViewers();
      addToast('success', 'Зритель заблокирован');
    } catch (e) {
      addToast('error', getApiErrorMessage(e));
    }
  };

  const exportViews = (videoId, format) => {
    fetch(`${API_URL}/api/videos/${videoId}/export-views?format=${format}`, { headers: authHeaders() })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `views_${videoId}.${format}`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch(() => addToast('error', 'Ошибка экспорта'));
  };

  const createPlaylist = async () => {
    if (!newPlaylistTitle.trim()) return;
    try {
      await axios.post(`${API_URL}/api/playlists`, {
        title: newPlaylistTitle.trim(),
        description: newPlaylistDescription.trim() || null,
        access_by_list: newPlaylistAccessByList,
        user_list_id: newPlaylistUserListId || null
      }, { headers: authHeaders() });
      setNewPlaylistTitle('');
      setNewPlaylistDescription('');
      setNewPlaylistAccessByList(false);
      setNewPlaylistUserListId('');
      fetchPlaylists().catch(() => {});
    } catch (e) {
      if (e.response?.status === 401) return;
      addToast('error', getApiErrorMessage(e));
    }
  };

  const openEditPlaylist = (playlistId) => {
    axios.get(`${API_URL}/api/playlists/${playlistId}`, { headers: authHeaders() })
      .then((r) => setEditingPlaylist(r.data))
      .catch(() => addToast('error', 'Ошибка загрузки'));
  };

  const updatePlaylist = async () => {
    if (!editingPlaylist) return;
    try {
      await axios.put(`${API_URL}/api/playlists/${editingPlaylist.id}`, {
        title: editingPlaylist.title,
        description: editingPlaylist.description || null,
        access_by_list: editingPlaylist.access_by_list,
        user_list_id: editingPlaylist.user_list_id || null
      }, { headers: authHeaders() });
      const items = (editingPlaylist.videos || []).map((v, i) => ({ video_id: v.id, order: i }));
      let orderSaved = true;
      try {
        await axios.post(`${API_URL}/api/playlists/${editingPlaylist.id}/videos`, items, { headers: authHeaders() });
      } catch (postErr) {
        if (postErr.response?.status === 401) return;
        orderSaved = false;
      }
      setEditingPlaylist(null);
      fetchPlaylists().catch(() => {});
      addToast('success', orderSaved ? 'Плейлист сохранён' : 'Плейлист обновлён. Порядок видео мог не сохраниться.');
    } catch (e) {
      if (e.response?.status === 401) return;
      addToast('error', getApiErrorMessage(e));
    }
  };

  const deletePlaylist = async (playlistId) => {
    if (!window.confirm('Удалить плейлист?')) return;
    try {
      await axios.delete(`${API_URL}/api/playlists/${playlistId}`, { headers: authHeaders() });
      setEditingPlaylist(null);
      fetchPlaylists().catch(() => {});
      addToast('success', 'Плейлист удалён');
    } catch (e) {
      if (e.response?.status === 401) return;
      addToast('error', getApiErrorMessage(e));
    }
  };

  const removeVideoFromPlaylist = (videoId) => {
    if (!editingPlaylist) return;
    setEditingPlaylist({
      ...editingPlaylist,
      videos: (editingPlaylist.videos || []).filter((v) => v.id !== videoId)
    });
  };

  const addVideoToEditingPlaylist = (videoId) => {
    if (!editingPlaylist) return;
    const v = videos.find((x) => x.id === videoId);
    if (!v || (editingPlaylist.videos || []).some((x) => x.id === videoId)) return;
    setEditingPlaylist({
      ...editingPlaylist,
      videos: [...(editingPlaylist.videos || []), { id: v.id, title: v.title, order: (editingPlaylist.videos || []).length, duration: v.duration }]
    });
  };

  const getPlaylistLink = (playlistId) => `${getBaseUrl()}/playlist/${playlistId}`;

  const openEditUserList = (listId) => {
    axios.get(`${API_URL}/api/user-lists/${listId}`, { headers: authHeaders() })
      .then((r) => setEditingUserList(r.data))
      .catch(() => addToast('error', 'Ошибка загрузки'));
  };

  const updateUserListName = async () => {
    if (!editingUserList) return;
    try {
      await axios.put(`${API_URL}/api/user-lists/${editingUserList.id}`, { name: editingUserList.name }, { headers: authHeaders() });
      setEditingUserList({ ...editingUserList, name: editingUserList.name });
      fetchUserLists().catch(() => {});
      addToast('success', 'Список обновлён');
    } catch (e) {
      if (e.response?.status === 401) return;
      addToast('error', getApiErrorMessage(e));
    }
  };

  const addUserListItem = async () => {
    if (!editingUserList || !newListItemEmail.trim()) return;
    const payload = { full_name: newListItemFullName.trim() || '', email: newListItemEmail.trim() };
    try {
      await axios.post(`${API_URL}/api/user-lists/${editingUserList.id}/items`, payload, {
        headers: { ...authHeaders(), 'Content-Type': 'application/json' }
      });
      setNewListItemFullName('');
      setNewListItemEmail('');
      const r = await axios.get(`${API_URL}/api/user-lists/${editingUserList.id}`, { headers: authHeaders() });
      setEditingUserList(r.data);
      addToast('success', 'Запись добавлена');
    } catch (e) {
      if (e.response?.status === 401) return;
      addToast('error', getApiErrorMessage(e));
    }
  };

  const deleteUserListItem = async (itemId) => {
    if (!editingUserList) return;
    try {
      await axios.delete(`${API_URL}/api/user-lists/${editingUserList.id}/items/${itemId}`, { headers: authHeaders() });
      const r = await axios.get(`${API_URL}/api/user-lists/${editingUserList.id}`, { headers: authHeaders() });
      setEditingUserList(r.data);
      setEditingListItemId(null);
      fetchUserLists().catch(() => {});
      addToast('success', 'Запись удалена');
    } catch (e) {
      if (e.response?.status === 401) return;
      addToast('error', getApiErrorMessage(e));
    }
  };

  const startEditListItem = (item) => {
    setEditingListItemId(item.id);
    setEditingListItemForm({ full_name: item.full_name || '', email: item.email || '' });
  };

  const saveEditListItem = async () => {
    if (!editingUserList || !editingListItemId) return;
    try {
      await axios.put(`${API_URL}/api/user-lists/${editingUserList.id}/items/${editingListItemId}`, editingListItemForm, { headers: authHeaders() });
      const r = await axios.get(`${API_URL}/api/user-lists/${editingUserList.id}`, { headers: authHeaders() });
      setEditingUserList(r.data);
      setEditingListItemId(null);
      fetchUserLists().catch(() => {});
      addToast('success', 'Запись сохранена');
    } catch (e) {
      if (e.response?.status === 401) return;
      addToast('error', getApiErrorMessage(e));
    }
  };

  const saveWatermarkSettings = async () => {
    try {
      const r = await axios.put(`${API_URL}/api/admin/settings/watermark`, watermarkSettings, { headers: authHeaders() });
      setWatermarkSettings(r.data);
      addToast('success', 'Настройки метки сохранены');
    } catch (e) {
      if (e.response?.status === 401) return;
      addToast('error', getApiErrorMessage(e));
    }
  };

  const deleteUserList = async (listId) => {
    if (!window.confirm('Удалить список и все записи?')) return;
    try {
      await axios.delete(`${API_URL}/api/user-lists/${listId}`, { headers: authHeaders() });
      setEditingUserList(null);
      fetchUserLists().catch(() => {});
      addToast('success', 'Список удалён');
    } catch (e) {
      if (e.response?.status === 401) return;
      addToast('error', getApiErrorMessage(e));
    }
  };

  return (
    <div className="admin-app">
      <header className="admin-header">
        <div className="header-inner">
          <a className="header-brand" href="#/" onClick={(e) => { e.preventDefault(); setActiveTab('videos'); }}>
            <span className="header-logo"><BrandMark size={34} /></span>
            <span className="header-title">{BRAND_NAME}<small>Студия</small></span>
          </a>
          <nav className="header-nav">
            <button type="button" className={activeTab === 'videos' ? 'active' : ''} onClick={() => setActiveTab('videos')}>Видео</button>
            <button type="button" className={activeTab === 'playlists' ? 'active' : ''} onClick={() => setActiveTab('playlists')}>Плейлисты</button>
            <button type="button" className={activeTab === 'lists' ? 'active' : ''} onClick={() => setActiveTab('lists')}>Списки</button>
            <button type="button" className={activeTab === 'security' ? 'active' : ''} onClick={() => setActiveTab('security')}>Безопасность</button>
            <button type="button" className={activeTab === 'watermark' ? 'active' : ''} onClick={() => { setActiveTab('watermark'); axios.get(`${API_URL}/api/admin/settings/watermark`, { headers: authHeaders() }).then((r) => setWatermarkSettings(r.data)).catch(() => {}); }}>Метка</button>
            <button type="button" className={activeTab === 'logs' ? 'active' : ''} onClick={() => { setActiveTab('logs'); fetchViewLogs(); }}>Логи</button>
            <button type="button" className={activeTab === 'suspicious' ? 'active' : ''} onClick={() => { setActiveTab('suspicious'); fetchSuspicious(); }}>Подозрительные</button>
          </nav>
          <ThemeToggle />
          <button type="button" onClick={logout} className="header-logout">Выйти</button>
        </div>
      </header>

      <div className="admin-container">
        {activeTab === 'videos' && (
        <>
        <div className="stats-bar">
          <div className="stat-card">
            <div className="stat-value">{stats.total}</div>
            <div className="stat-label">Всего видео</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.ready}</div>
            <div className="stat-label">Готово</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{stats.totalViews}</div>
            <div className="stat-label">Просмотров</div>
          </div>
        </div>

        <section className="upload-section">
          <h2>📤 Загрузить новое видео</h2>
          <form onSubmit={uploadVideo} className="upload-form">
            <div className="form-row">
              <input
                type="file"
                accept="video/*"
                onChange={(e) => setUploadFile(e.target.files[0])}
                required
                className="file-input"
              />
            </div>
            <div className="form-row">
              <input
                type="text"
                placeholder="Название видео"
                value={uploadTitle}
                onChange={(e) => setUploadTitle(e.target.value)}
                className="text-input"
              />
            </div>
            <div className="form-row">
              <textarea
                placeholder="Описание (необязательно)"
                value={uploadDescription}
                onChange={(e) => setUploadDescription(e.target.value)}
                className="textarea-input"
                rows="3"
              />
            </div>
            {loading && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${uploadProgress}%` }}
                  ></div>
                </div>
                <span className="progress-text">{uploadProgress}%</span>
              </div>
            )}
            <button type="submit" disabled={loading} className="upload-btn">
              {loading ? `⏳ Загрузка... ${uploadProgress}%` : '📤 Загрузить видео'}
            </button>
          </form>
        </section>

        <section className="videos-section">
          <div className="section-header">
            <h2>📹 Видео ({filteredVideos.length})</h2>
            <input
              type="text"
              placeholder="Поиск..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
          </div>
          <div className="videos-grid">
            {filteredVideos.map((video) => (
              <div key={video.id} className="video-card">
                <div className="video-card-header">
                  <h3>{video.title}</h3>
                  <span className={`status-badge status-${video.status}`}>
                    {video.status === 'ready' ? 'Готово' :
                     video.status === 'processing' ? 'Обработка' :
                     video.status === 'uploaded' ? 'Загружено' : 'Ошибка'}
                  </span>
                </div>
                {video.description && (
                  <p className="video-description">{video.description}</p>
                )}
                <div className="video-meta">
                  <span className="meta-item">👁️ {video.views || 0}</span>
                  {video.duration && (
                    <span className="meta-item">⏱️ {Math.floor(video.duration / 60)}:{(video.duration % 60).toString().padStart(2, '0')}</span>
                  )}
                  {video.file_size && (
                    <span className="meta-item">💾 {(video.file_size / (1024 * 1024)).toFixed(1)} MB</span>
                  )}
                  <span className="meta-item">📅 {new Date(video.created_at).toLocaleDateString('ru-RU')}</span>
                  {video.embed_only && <span className="meta-item" title="Только через iframe, прямой доступ заблокирован">🔒 iframe-only</span>}
                  {video.access_by_list && <span className="meta-item" title="Доступ по списку">📋 По списку</span>}
                </div>
                {(video.status === 'processing' || video.status === 'uploaded') && video.estimated_processing_time && (
                  <div className="processing-info">
                    {video.status === 'processing' && video.processing_started_at ? (
                      <>
                        <div className="processing-time">
                          <span className="processing-label">⏳ Обработка:</span>
                          <span className="processing-value">
                            {(() => {
                              const started = new Date(video.processing_started_at);
                              const now = new Date();
                              const elapsed = Math.floor((now - started) / 1000); // seconds
                              const remaining = Math.max(0, video.estimated_processing_time - elapsed);
                              const mins = Math.floor(remaining / 60);
                              const secs = remaining % 60;
                              return `~${mins}:${secs.toString().padStart(2, '0')}`;
                            })()}
                          </span>
                        </div>
                        <div className="processing-progress">
                          <div className="processing-progress-bar">
                            <div 
                              className="processing-progress-fill" 
                              style={{
                                width: `${Math.min(100, ((new Date() - new Date(video.processing_started_at)) / 1000 / video.estimated_processing_time) * 100)}%`
                              }}
                            ></div>
                          </div>
                        </div>
                      </>
                    ) : (
                      <div className="processing-time">
                        <span className="processing-label">⏱️ Ожидаемое время обработки:</span>
                        <span className="processing-value">
                          ~{Math.floor(video.estimated_processing_time / 60)}:{(video.estimated_processing_time % 60).toString().padStart(2, '0')}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                <div className="video-actions">
                  {video.status === 'ready' && (
                    <>
                      <button onClick={() => {
                        const link = getPublicLink(video.id);
                        navigator.clipboard.writeText(link);
                        addToast('success', 'Ссылка скопирована');
                      }} className="action-btn primary">
                        🔗 Публичная ссылка
                      </button>
                      <button onClick={() => getEmbedCode(video.id)} className="action-btn">
                        📝 Код встраивания
                      </button>
                      <button onClick={() => getDetailedStats(video.id)} className="action-btn">
                        📊 Статистика
                      </button>
                    </>
                  )}
                  <button onClick={() => setEditingVideo(video)} className="action-btn">
                    ✏️ Редактировать
                  </button>
                  {video.status !== 'ready' && (
                    <button onClick={() => triggerConvert(video.id)} className="action-btn">
                      🔄 Конвертировать
                    </button>
                  )}
                  <button onClick={() => deleteVideo(video.id)} className="action-btn delete">
                    🗑️ Удалить
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {selectedVideo && (
          <div className="modal" onClick={() => setSelectedVideo(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Код встраивания</h2>

              <div className="modal-section">
                <label>Прямая ссылка на просмотр:</label>
                <code className="code-block">{selectedVideo.direct_url}</code>
                <button onClick={() => {
                  navigator.clipboard.writeText(selectedVideo.direct_url);
                  addToast('success', 'Ссылка скопирована');
                }} className="copy-btn">📋 Копировать ссылку</button>
              </div>

              <div className="modal-section">
                <label>
                  🎬 Код встраивания — чистый плеер <span style={{fontSize:'0.75rem', color:'#9a9aaa', fontWeight:'normal'}}>(рекомендуется)</span>
                </label>
                <p style={{fontSize:'0.78rem', color:'#9a9aaa', marginBottom:'0.5rem'}}>
                  Встраивает только видео без оболочки сайта. Поддерживает выбор качества.
                </p>
                <textarea
                  readOnly
                  value={selectedVideo.embed_code}
                  onClick={(e) => e.target.select()}
                  className="code-textarea"
                  rows={3}
                />
                <button onClick={() => {
                  navigator.clipboard.writeText(selectedVideo.embed_code);
                  addToast('success', 'Код скопирован');
                }} className="copy-btn">📋 Копировать код</button>
              </div>

              <div className="modal-section">
                <label>
                  🌐 Код встраивания — полная страница плеера
                </label>
                <p style={{fontSize:'0.78rem', color:'#9a9aaa', marginBottom:'0.5rem'}}>
                  Встраивает полную страницу с плеером (переключение видео в плейлисте, водяной знак и пр.).
                </p>
                <textarea
                  readOnly
                  value={selectedVideo.page_embed_code}
                  onClick={(e) => e.target.select()}
                  className="code-textarea"
                  rows={3}
                />
                <button onClick={() => {
                  navigator.clipboard.writeText(selectedVideo.page_embed_code);
                  addToast('success', 'Код скопирован');
                }} className="copy-btn">📋 Копировать код</button>
              </div>

              <button onClick={() => setSelectedVideo(null)} className="close-btn">Закрыть</button>
            </div>
          </div>
        )}

        {editingVideo && (
          <div className="modal" onClick={() => setEditingVideo(null)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>Редактировать видео</h2>
              <div className="edit-form">
                <label>Название:</label>
                <input
                  type="text"
                  value={editingVideo.title}
                  onChange={(e) => setEditingVideo({...editingVideo, title: e.target.value})}
                  className="text-input"
                />
                <label>Описание:</label>
                <textarea
                  value={editingVideo.description || ''}
                  onChange={(e) => setEditingVideo({...editingVideo, description: e.target.value})}
                  className="textarea-input"
                  rows="5"
                />
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!!editingVideo.embed_only}
                    onChange={(e) => setEditingVideo({...editingVideo, embed_only: e.target.checked})}
                  />
                  Только через iframe (блокировать прямой доступ по ссылке)
                </label>
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={!!editingVideo.access_by_list}
                    onChange={(e) => setEditingVideo({...editingVideo, access_by_list: e.target.checked})}
                  />
                  Доступ по спискам пользователей
                </label>
                {editingVideo.access_by_list && (
                  <>
                    <label>Список пользователей:</label>
                    <select
                      value={editingVideo.user_list_id || ''}
                      onChange={(e) => setEditingVideo({...editingVideo, user_list_id: e.target.value || null})}
                      className="text-input"
                    >
                      <option value="">— Выберите список —</option>
                      {userLists.map((l) => (
                        <option key={l.id} value={l.id}>{l.name} ({l.items_count || 0})</option>
                      ))}
                    </select>
                  </>
                )}
                <div className="modal-actions">
                  <button onClick={() => updateVideo(editingVideo.id, editingVideo.title, editingVideo.description, editingVideo.access_by_list, editingVideo.user_list_id, editingVideo.embed_only)} className="save-btn">
                    💾 Сохранить
                  </button>
                  <button onClick={() => setEditingVideo(null)} className="cancel-btn">Отмена</button>
                </div>
              </div>
            </div>
          </div>
        )}

        {detailedStats && (
          <div className="modal" onClick={() => setDetailedStats(null)}>
            <div className="modal-content stats-modal-content" onClick={(e) => e.stopPropagation()}>
              <h2>📊 Детальная статистика</h2>
              
              <div className="stats-grid">
                <div className="stats-detail-card">
                  <div className="stats-detail-value">{detailedStats.total_views}</div>
                  <div className="stats-detail-label">Всего просмотров</div>
                </div>
                <div className="stats-detail-card">
                  <div className="stats-detail-value">{detailedStats.unique_views}</div>
                  <div className="stats-detail-label">Уникальных просмотров</div>
                </div>
                <div className="stats-detail-card">
                  <div className="stats-detail-value">{Math.floor(detailedStats.total_watch_time / 60)}</div>
                  <div className="stats-detail-label">Минут просмотрено</div>
                </div>
                <div className="stats-detail-card">
                  <div className="stats-detail-value">{Math.floor(detailedStats.average_watch_time)}с</div>
                  <div className="stats-detail-label">Среднее время</div>
                </div>
                <div className="stats-detail-card">
                  <div className="stats-detail-value">{detailedStats.views_today}</div>
                  <div className="stats-detail-label">Сегодня</div>
                </div>
                <div className="stats-detail-card">
                  <div className="stats-detail-value">{detailedStats.views_this_week}</div>
                  <div className="stats-detail-label">Эта неделя</div>
                </div>
                <div className="stats-detail-card">
                  <div className="stats-detail-value">{detailedStats.views_this_month}</div>
                  <div className="stats-detail-label">Этот месяц</div>
                </div>
              </div>

              {detailedStats.views_by_date && detailedStats.views_by_date.length > 0 && (
                <div className="views-chart">
                  <h3>Просмотры за последние 30 дней</h3>
                  <div className="chart-bar">
                    {(() => {
                      const last7 = detailedStats.views_by_date.slice(-7);
                      const maxViews = Math.max(...last7.map(v => v.views), 1);
                      return last7.map((item, index) => {
                        const height = (item.views / maxViews) * 100;
                        return (
                          <div key={index} className="chart-column">
                            <div
                              className="chart-item"
                              style={{ height: `${height}%` }}
                              title={`${item.views} просмотров`}
                            ></div>
                            <div className="chart-label">{new Date(item.date).toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' })}</div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                </div>
              )}

              <div className="stats-export">
                <button onClick={() => exportViews(detailedStats.video_id, 'xlsx')} className="action-btn">📥 Экспорт XLSX</button>
                <button onClick={() => exportViews(detailedStats.video_id, 'csv')} className="action-btn">📥 Экспорт CSV</button>
              </div>

              <button onClick={() => setDetailedStats(null)} className="close-btn">Закрыть</button>
            </div>
          </div>
        )}

        </>)}

        {activeTab === 'playlists' && (
          <section className="admin-section">
            <h2>Плейлисты</h2>
            <p className="section-desc">Создавайте плейлисты, добавляйте видео, настраивайте доступ по списку или без.</p>
            <div className="playlist-create-form">
              <h3>Создать плейлист</h3>
              <div className="form-row">
                <input type="text" placeholder="Название" value={newPlaylistTitle} onChange={(e) => setNewPlaylistTitle(e.target.value)} className="text-input" />
                <textarea placeholder="Описание (необяз.)" value={newPlaylistDescription} onChange={(e) => setNewPlaylistDescription(e.target.value)} className="textarea-input" rows="2" />
              </div>
              <label className="checkbox-label">
                <input type="checkbox" checked={newPlaylistAccessByList} onChange={(e) => setNewPlaylistAccessByList(e.target.checked)} />
                Доступ по спискам пользователей
              </label>
              {newPlaylistAccessByList && (
                <select value={newPlaylistUserListId} onChange={(e) => setNewPlaylistUserListId(e.target.value)} className="text-input">
                  <option value="">— Выберите список —</option>
                  {userLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
              <button type="button" onClick={createPlaylist} className="upload-btn">Создать плейлист</button>
            </div>
            <h3>Плейлисты</h3>
            <div className="card-list">
              {playlists.map((p) => (
                <div key={p.id} className="card-item card-item-actions">
                  <div className="card-item-main">
                    <strong>{p.title}</strong>
                    <span>{p.videos_count || 0} видео</span>
                    {p.access_by_list && <span className="badge-list">По списку</span>}
                  </div>
                  <div className="card-item-buttons">
                    <button type="button" onClick={() => { const link = getPlaylistLink(p.id); navigator.clipboard.writeText(link); addToast('success', 'Ссылка скопирована'); }} className="action-btn">🔗 Ссылка</button>
                    <button type="button" onClick={() => openEditPlaylist(p.id)} className="action-btn">✏️ Редактировать</button>
                    <button type="button" onClick={() => deletePlaylist(p.id)} className="action-btn delete">🗑️ Удалить</button>
                  </div>
                </div>
              ))}
            </div>

            {editingPlaylist && (
              <div className="modal" onClick={() => setEditingPlaylist(null)}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                  <h2>Редактировать плейлист</h2>
                  <div className="edit-form">
                    <label>Название:</label>
                    <input type="text" value={editingPlaylist.title} onChange={(e) => setEditingPlaylist({ ...editingPlaylist, title: e.target.value })} className="text-input" />
                    <label>Описание:</label>
                    <textarea value={editingPlaylist.description || ''} onChange={(e) => setEditingPlaylist({ ...editingPlaylist, description: e.target.value })} className="textarea-input" rows="3" />
                    <label className="checkbox-label">
                      <input type="checkbox" checked={!!editingPlaylist.access_by_list} onChange={(e) => setEditingPlaylist({ ...editingPlaylist, access_by_list: e.target.checked })} />
                      Доступ по спискам пользователей
                    </label>
                    {editingPlaylist.access_by_list && (
                      <select value={editingPlaylist.user_list_id || ''} onChange={(e) => setEditingPlaylist({ ...editingPlaylist, user_list_id: e.target.value || null })} className="text-input">
                        <option value="">— Выберите список —</option>
                        {userLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
                      </select>
                    )}
                    <label>Видео в плейлисте:</label>
                    <ul className="playlist-edit-videos">
                      {(editingPlaylist.videos || []).map((v, i) => (
                        <li key={v.id}>
                          <span>{i + 1}. {v.title}</span>
                          <button type="button" onClick={() => removeVideoFromPlaylist(v.id)} className="delete-btn-small">Удалить</button>
                        </li>
                      ))}
                    </ul>
                    <label>Добавить видео:</label>
                    <div className="form-row">
                      <select className="text-input" onChange={(e) => { const id = e.target.value; if (id) { addVideoToEditingPlaylist(id); e.target.value = ''; } }}>
                        <option value="">— Выберите видео —</option>
                        {videos.filter((v) => v.status === 'ready' && !(editingPlaylist.videos || []).some((pv) => pv.id === v.id)).map((v) => (
                          <option key={v.id} value={v.id}>{v.title}</option>
                        ))}
                      </select>
                    </div>
                    <div className="modal-actions">
                      <button onClick={updatePlaylist} className="save-btn">💾 Сохранить</button>
                      <button onClick={() => setEditingPlaylist(null)} className="cancel-btn">Отмена</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === 'lists' && (
          <section className="admin-section">
            <h2>Списки пользователей</h2>
            <p className="section-desc">Создавайте списки, загружайте XLSX (ФИО, Email), редактируйте и удаляйте записи. Список можно привязать к видео или плейлисту.</p>
            <div className="template-download">
              <button type="button" onClick={downloadTemplate} className="action-btn">📥 Скачать шаблон XLSX</button>
            </div>
            <div className="form-row">
              <input type="text" placeholder="Название списка" value={newListName} onChange={(e) => setNewListName(e.target.value)} className="text-input" />
              <button type="button" onClick={createUserList} className="upload-btn">Создать список</button>
            </div>
            <div className="upload-xlsx-row">
              <select value={selectedListForUpload || ''} onChange={(e) => setSelectedListForUpload(e.target.value || null)} className="text-input">
                <option value="">— Выберите список —</option>
                {userLists.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
              </select>
              <input type="file" accept=".xlsx,.xls" onChange={(e) => setXlsxFile(e.target.files?.[0])} />
              <button type="button" onClick={uploadXlsx} className="action-btn">Загрузить XLSX</button>
            </div>
            <h3>Списки</h3>
            <div className="card-list">
              {userLists.map((l) => (
                <div key={l.id} className="card-item card-item-actions">
                  <div className="card-item-main">
                    <strong>{l.name}</strong>
                    <span>{l.items_count || 0} записей</span>
                  </div>
                  <div className="card-item-buttons">
                    <button type="button" onClick={() => openEditUserList(l.id)} className="action-btn">✏️ Редактировать</button>
                    <button type="button" onClick={() => deleteUserList(l.id)} className="action-btn delete">🗑️ Удалить</button>
                  </div>
                </div>
              ))}
            </div>

            {editingUserList && (
              <div className="modal" onClick={() => { setEditingUserList(null); setEditingListItemId(null); }}>
                <div className="modal-content modal-content-wide" onClick={(e) => e.stopPropagation()}>
                  <h2>Редактировать список: {editingUserList.name}</h2>
                  <div className="edit-form">
                    <label>Название списка:</label>
                    <div className="form-row">
                      <input type="text" value={editingUserList.name} onChange={(e) => setEditingUserList({ ...editingUserList, name: e.target.value })} className="text-input" />
                      <button type="button" onClick={updateUserListName} className="action-btn">Сохранить название</button>
                    </div>
                    <label>Записи ({editingUserList.items?.length || 0}):</label>
                    <ul className="user-list-items-edit">
                      {(editingUserList.items || []).map((item) => (
                        <li key={item.id}>
                          {editingListItemId === item.id ? (
                            <>
                              <input type="text" placeholder="ФИО" value={editingListItemForm.full_name} onChange={(e) => setEditingListItemForm((f) => ({ ...f, full_name: e.target.value }))} className="text-input" style={{ flex: 1 }} />
                              <input type="email" placeholder="Email" value={editingListItemForm.email} onChange={(e) => setEditingListItemForm((f) => ({ ...f, email: e.target.value }))} className="text-input" style={{ flex: 1 }} />
                              <button type="button" onClick={saveEditListItem} className="action-btn">Сохранить</button>
                              <button type="button" onClick={() => setEditingListItemId(null)} className="cancel-btn">Отмена</button>
                            </>
                          ) : (
                            <>
                              <span className="item-fullname">{item.full_name}</span>
                              <span className="item-email">{item.email}</span>
                              <button type="button" onClick={() => startEditListItem(item)} className="action-btn">✏️</button>
                              <button type="button" onClick={() => deleteUserListItem(item.id)} className="delete-btn-small">Удалить</button>
                            </>
                          )}
                        </li>
                      ))}
                    </ul>
                    <label>Добавить запись:</label>
                    <div className="form-row">
                      <input type="text" placeholder="ФИО" value={newListItemFullName} onChange={(e) => setNewListItemFullName(e.target.value)} className="text-input" />
                      <input type="email" placeholder="Email" value={newListItemEmail} onChange={(e) => setNewListItemEmail(e.target.value)} className="text-input" />
                      <button type="button" onClick={addUserListItem} className="action-btn">Добавить</button>
                    </div>
                    <div className="modal-actions" style={{ marginTop: 16 }}>
                      <button type="button" onClick={() => deleteUserList(editingUserList.id)} className="cancel-btn" style={{ marginRight: 'auto', background: 'var(--color-btn-danger-bg)', color: 'white' }}>🗑️ Удалить список полностью</button>
                      <button onClick={() => { setEditingUserList(null); setEditingListItemId(null); }} className="cancel-btn">Закрыть</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>
        )}

        {activeTab === 'security' && (
          <section className="admin-section">
            <h2>Безопасность</h2>
            <h3>Разрешённые домены для iframe</h3>
            <p className="section-desc">Только эти домены смогут встраивать видео через iframe.</p>
            <div className="form-row">
              <input type="text" placeholder="example.com" value={newDomain} onChange={(e) => setNewDomain(e.target.value)} className="text-input" />
              <button type="button" onClick={addAllowedDomain} className="action-btn">Добавить</button>
            </div>
            <ul className="domain-list">
              {allowedDomains.map((d) => (
                <li key={d.id}>
                  {d.domain}
                  <button type="button" onClick={() => axios.delete(`${API_URL}/api/admin/allowed-domains/${d.id}`, { headers: authHeaders() }).then(fetchAllowedDomains).catch((e) => addToast('error', 'Ошибка удаления: ' + getApiErrorMessage(e)))} className="delete-btn-small">Удалить</button>
                </li>
              ))}
            </ul>
            <h3>Заблокированные зрители</h3>
            <p className="section-desc">Им будет отказано в доступе по ФИО+Email.</p>
            <div className="form-row">
              <input type="text" placeholder="Email" value={newBlockedEmail} onChange={(e) => setNewBlockedEmail(e.target.value)} className="text-input" />
              <input type="text" placeholder="ФИО (необяз.)" value={newBlockedName} onChange={(e) => setNewBlockedName(e.target.value)} className="text-input" />
              <button type="button" onClick={blockViewer} className="action-btn">Заблокировать</button>
            </div>
            <ul className="domain-list">
              {blockedViewers.map((b) => (
                <li key={b.id}>
                  {b.email} {b.full_name && `(${b.full_name})`}
                  <button type="button" onClick={() => axios.delete(`${API_URL}/api/admin/blocked-viewers/${b.id}`, { headers: authHeaders() }).then(fetchBlockedViewers).catch((e) => addToast('error', 'Ошибка: ' + getApiErrorMessage(e)))} className="delete-btn-small">Разблокировать</button>
                </li>
              ))}
            </ul>
          </section>
        )}

        {activeTab === 'watermark' && (
          <section className="admin-section">
            <h2>Двигающаяся метка на видео</h2>
            <p className="section-desc">Настройки водяного знака (ФИО · Email · IP), который показывается на видео с доступом по списку. Метка периодически меняет положение.</p>
            <div className="watermark-settings">
              <label className="login-label">
                <span>Интервал перемещения (сек)</span>
                <input
                  type="number"
                  min={5}
                  max={120}
                  value={watermarkSettings.move_interval_seconds}
                  onChange={(e) => setWatermarkSettings((s) => ({ ...s, move_interval_seconds: parseInt(e.target.value, 10) || 15 }))}
                  className="text-input"
                />
              </label>
              <label className="checkbox-label">
                <input
                  type="checkbox"
                  checked={watermarkSettings.show_ip}
                  onChange={(e) => setWatermarkSettings((s) => ({ ...s, show_ip: e.target.checked }))}
                />
                <span>Показывать IP в метке</span>
              </label>
              <label className="login-label">
                <span>Прозрачность текста (0.2 — 1)</span>
                <input
                  type="number"
                  min={0.2}
                  max={1}
                  step={0.1}
                  value={watermarkSettings.opacity}
                  onChange={(e) => setWatermarkSettings((s) => ({ ...s, opacity: parseFloat(e.target.value) || 0.6 }))}
                  className="text-input"
                />
              </label>
              <label className="login-label">
                <span>Размер шрифта (px)</span>
                <input
                  type="number"
                  min={10}
                  max={24}
                  value={watermarkSettings.font_size}
                  onChange={(e) => setWatermarkSettings((s) => ({ ...s, font_size: parseInt(e.target.value, 10) || 12 }))}
                  className="text-input"
                />
              </label>
              <button type="button" onClick={saveWatermarkSettings} className="upload-btn">Сохранить настройки метки</button>
            </div>
          </section>
        )}

        {activeTab === 'logs' && (
          <section className="admin-section">
            <h2>Логи просмотров</h2>
            <p className="section-desc">Подробная статистика: видео, зритель, IP, User-Agent, время просмотра.</p>
            <div className="view-logs-toolbar">
              <select value={viewLogsVideoFilter} onChange={(e) => setViewLogsVideoFilter(e.target.value)} className="text-input">
                <option value="">Все видео</option>
                {videos.filter((v) => v.status === 'ready').map((v) => (
                  <option key={v.id} value={v.id}>{v.title}</option>
                ))}
              </select>
              <button type="button" onClick={fetchViewLogs} className="action-btn">Обновить</button>
            </div>
            <div className="view-logs-table-wrap">
              <table className="view-logs-table">
                <thead>
                  <tr>
                    <th>Дата и время</th>
                    <th>Видео</th>
                    <th>ФИО</th>
                    <th>Email</th>
                    <th>IP</th>
                    <th>User-Agent</th>
                    <th>Просмотрено (сек)</th>
                    <th>%</th>
                    <th>Повтор</th>
                  </tr>
                </thead>
                <tbody>
                  {viewLogs.map((log) => (
                    <tr key={log.id}>
                      <td>{new Date(log.viewed_at).toLocaleString('ru-RU')}</td>
                      <td>{log.video_title || log.video_id}</td>
                      <td>{log.viewer_name || '—'}</td>
                      <td>{log.viewer_email || '—'}</td>
                      <td>{log.ip_address || '—'}</td>
                      <td className="ua-cell" title={log.user_agent}>{(log.user_agent || '—').slice(0, 50)}{(log.user_agent && log.user_agent.length > 50) ? '…' : ''}</td>
                      <td>{log.watch_time ?? '—'}</td>
                      <td>{log.percent_watched != null ? Math.round(log.percent_watched) + '%' : '—'}</td>
                      <td>{log.is_repeat ? 'Да' : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {viewLogs.length === 0 && <p className="section-desc">Нет записей или выберите видео и нажмите «Обновить».</p>}
          </section>
        )}

        {activeTab === 'suspicious' && (
          <section className="admin-section">
            <h2>Подозрительная активность</h2>
            <p className="section-desc">Один пользователь с разных IP; массовые подключения за 24 ч.</p>
            {suspicious && (
              <>
                <h3>Один пользователь — разные IP</h3>
                <ul className="suspicious-list">
                  {(suspicious.same_user_different_ips || []).map((item, i) => (
                    <li key={i}>{item.email} — {item.ip_count} IP</li>
                  ))}
                </ul>
                <h3>Массовые подключения за 24 ч</h3>
                <ul className="suspicious-list">
                  {(suspicious.mass_connections_24h || []).map((item, i) => (
                    <li key={i}>{item.ip} — {item.views} просмотров</li>
                  ))}
                </ul>
              </>
            )}
          </section>
        )}
      </div>
      <ToastList toasts={toasts} removeToast={removeToast} />
    </div>
  );
}

export default AdminPanel;

